export type * from "./NetworkEvent";
import type { NetworkEvent } from "./NetworkEvent";
import type { TabEvent } from "./TabEvents";
import type { WebNavigationEvent } from "./WebNavigationEvents";
import { InstrumentationEvent } from "./InstrumentationEvent";
import { DebuggerPageEvent, DebuggerPageEventName } from "./DebuggerPageEvent";
import { ObservedTabFields } from "./EventBase";
import type { DebuggerNetworkHttpEventName } from '@uppajung/hardy-har';
import type { ISODateTimeString } from '@uppajung/hardy-har/';
import { nowAsISODateTimeString } from "../../State/EvidenceDB/DatabaseTypes";
export type * from "./TabEvents";
export type * from "./WebNavigationEvents";
export * from "./EventBase";
export * from "./DebuggerPageEvent";

export { ISODateTimeString };

export interface HasEventIndex {
	eventIndex: number;
}

// export interface HasEventUuid {
// 	eventUuid: UUID;
// }

export interface HasWhenRecordedIso {
	/* The recording time as an ISO date string */
	readonly whenRecordedIso: ISODateTimeString;
	readonly whenUpdatedIso?: ISODateTimeString;
}

export interface HasTab {
	readonly tab: ObservedTabFields;
}

export const augmentRecordWithWhenRecordedNow = <T extends {}>(obj: T): T & HasWhenRecordedIso => {
	return {...obj, whenRecordedIso: nowAsISODateTimeString()};
}

export type RecordableEvent =
	NetworkEvent |
	InstrumentationEvent |
	TabEvent |
	WebNavigationEvent |
	DebuggerPageEvent |
//	DebuggerDOMEvent |
	never;

export type RecordableEventCategory = RecordableEvent["category"];
export type RecordableEventOfCategory<
	CATEGORY extends RecordableEventCategory
> = Extract<RecordableEvent, {category: CATEGORY}>;
export type RecordableEventNameWithinCategory<
	CATEGORY extends RecordableEventCategory
> = RecordableEventOfCategory<CATEGORY>["eventName"];
export type RecordableEventByCategoryAndName<
	CATEGORY extends RecordableEventCategory,
	NAME extends RecordableEventName,
> = RecordableEvent & Extract<RecordableEvent, {category: CATEGORY, eventName: NAME}>;

export type RecordableEventName<
	CATEGORY extends RecordableEventCategory = RecordableEventCategory
> = RecordableEventOfCategory<CATEGORY>["eventName"];


export const hasCategoryAndEventName = <
	CATEGORY extends RecordableEventCategory,
	EVENT_NAME extends RecordableEventName<CATEGORY>,
>(category: CATEGORY, eventName: EVENT_NAME) => <T extends {eventName: string, category: string}>(
	obj: T
): obj is Extract<T, {category: CATEGORY, eventName: EVENT_NAME}> =>
	obj.category === category && obj.eventName === eventName;


export const isRecordableEvent = (event: unknown): event is RecordableEvent => {
	if (event == null || typeof event !== 'object' ||
		!("category" in event) || typeof event.category !== "string" ||
		!("eventName" in event) || typeof event.eventName !== "string"
	) {
		return false;
	}
	return true;
}

const NetworkEventCategories = [
	"WebRequest",
	"Debugger.Network",
//	"DevTools.Network"
] satisfies NetworkEvent["category"][] as string[];

export const isEventNetworkEvent = <T extends RecordableEvent>(event: T): event is T & NetworkEvent =>
	NetworkEventCategories.includes(event.category);

export const isNetworkEvent = (event: unknown): event is NetworkEvent =>
	isRecordableEvent(event) && isEventNetworkEvent(event);


export const WebSocketEventsToRecordNames = [
	"Network.webSocketFrameSent",
	"Network.webSocketFrameReceived",
	"Network.webSocketCreated",
	"Network.webSocketClosed",
	"Network.webSocketWillSendHandshakeRequest",
	"Network.webSocketHandshakeResponseReceived",
] as const;


export const DebuggerNetworkHttpEventNames = [
	"Network.requestWillBeSent",
	"Network.requestServedFromCache",
	"Network.dataReceived",
	"Network.responseReceived",
	"Network.resourceChangedPriority",
	"Network.loadingFinished",
	"Network.loadingFailed",
	"Network.requestWillBeSentExtraInfo",
	"Network.responseReceivedExtraInfo",
] as const satisfies DebuggerNetworkHttpEventName[];

export const DevToolsEventsToRecordNames = [
	'DevTools.onRequestFinished',
]

export type DevToolsEventToRecordName = NonNullable<typeof DevToolsEventsToRecordNames>[number];
export const setOfNamesOfDevToolsEventsToRecord = new Set(DevToolsEventsToRecordNames);
export const isNameOfDevToolsEventsToRecord = (eventName: string): eventName is DevToolsEventToRecordName => setOfNamesOfDevToolsEventsToRecord.has(eventName as DevToolsEventToRecordName);

export type WebSocketEventToRecordName = NonNullable<typeof WebSocketEventsToRecordNames>[number];
export const setOfNamesOfWebSocketEventsToRecord = new Set(WebSocketEventsToRecordNames);
export const isNameOfWebSocketEventsToRecord = (eventName: string): eventName is WebSocketEventToRecordName => setOfNamesOfWebSocketEventsToRecord.has(eventName as WebSocketEventToRecordName);

export const HttpNetworkEventsToRecordNames = DebuggerNetworkHttpEventNames;
export type NameOfHttpNetworkEventToRecord = NonNullable<typeof HttpNetworkEventsToRecordNames>[number];
export const setOfNamesOfHttpNetworkEventsToRecord = new Set(HttpNetworkEventsToRecordNames);
export const isNameOfHttpNetworkEventsToRecord = (eventName: string): eventName is NameOfHttpNetworkEventToRecord => setOfNamesOfHttpNetworkEventsToRecord.has(eventName as NameOfHttpNetworkEventToRecord);

export const PageEventToRecordNames = [
	"Page.loadEventFired",
	"Page.domContentEventFired",
	"Page.frameStartedLoading",
	"Page.frameAttached",
	"Page.frameDetached",
	"Page.frameNavigated",
	"Page.frameScheduledNavigation",
	"Page.frameRequestedNavigation",
	"Page.navigatedWithinDocument",
	"Page.frameStartedNavigating",
] as const satisfies DebuggerPageEventName[];

export type NameOfPageEventToRecord = NonNullable<typeof PageEventToRecordNames>[number];
export const setOfNamesOfPageEventsToRecord = new Set(PageEventToRecordNames);
export const isNameOfPageEventToRecord = (eventName: string): eventName is NameOfPageEventToRecord => setOfNamesOfPageEventsToRecord.has(eventName as NameOfPageEventToRecord);

export const DebuggerEventToRecordNames = [
	...WebSocketEventsToRecordNames,
	...DebuggerNetworkHttpEventNames,
	...PageEventToRecordNames,
]
export type NameOfDebuggerEventToRecord = NonNullable<typeof DebuggerEventToRecordNames>[number];
export const setOfNamesOfDebuggerEventsToRecord = new Set(DebuggerEventToRecordNames);
export const isNameOfDebuggerEventToRecord = (eventName: string): eventName is NameOfDebuggerEventToRecord => setOfNamesOfDebuggerEventsToRecord.has(eventName as NameOfDebuggerEventToRecord);